import {
  client,
  UpsertDemandModifierRequest,
  DemandModifier,
} from '../../../../app/services/api/orderManagementClient';
import {
  DemandModifierLocationType,
  DemandModifierTargetType,
  DemandModifierType,
} from '../types/DemandModifierTypes';

interface UseSaveDemandModifierOptions {
  storeNumber: string | undefined;
}

export interface SaveDemandModifierResult {
  success: boolean;
  message?: string;
}

/**
 * Shared hook that builds a UpsertDemandModifierRequest from a DemandModifier
 */
export const useSaveDemandModifier = ({ storeNumber }: UseSaveDemandModifierOptions) => {
  const buildRequest = (modifier: DemandModifier): UpsertDemandModifierRequest => {
    const modifierValue = modifier.modifierType === DemandModifierType.PercentageUplift
      ? modifier.upliftPercentage
      : (modifier.quantityOffset ?? 0);

    const targetCategories = modifier.targetType === DemandModifierTargetType.SpecificCategories
      ? modifier.targetCategories
      : [];
    const targetSKUs = modifier.targetType === DemandModifierTargetType.SpecificSKUs
      ? modifier.targetSKUs
      : [];
    const targetFranchises = modifier.locationType === DemandModifierLocationType.SpecificFranchises
      ? modifier.targetFranchises
      : [];
    const targetStores = modifier.locationType === DemandModifierLocationType.SpecificStores
      ? modifier.targetStores
      : [];

    return {
      id: modifier.id,
      name: modifier.name,
      startDate: modifier.startDate,
      endDate: modifier.endDate,
      modifierType: modifier.modifierType,
      modifierValue,
      itemTargetType: modifier.targetType,
      targetCategories,
      targetSKUs,
      locationTargetType: modifier.locationType,
      targetFranchises,
      targetStores,
      isActive: modifier.isActive,
    };
  };

  /**
   * Saves (creates or updates) a demand modifier.
   * @returns `true` when the API call succeeded, `false` when storeNumber is
   * missing or the API returns a non-success result. Exceptions are not caught
   * here — the caller is responsible for error handling / snackbars.
   */
  const saveDemandModifier = async (modifier: DemandModifier): Promise<SaveDemandModifierResult> => {
    if (!storeNumber) return { success: false, message: 'Store number is missing.' };

    const request = buildRequest(modifier);

    if (modifier.id) {
      const result = await client.updateDemandModifier(storeNumber, request);
      return { success: result.success, message: result.message };
    } else {
      const result = await client.createDemandModifier(storeNumber, request);
      return { success: result.success, message: result.message };
    }
  };

  return { saveDemandModifier };
};
